/**
 * Crew Module Helper Functions
 * Utility functions for compliance calculations, exports, and data processing
 */

import { EXPIRY_THRESHOLDS, HOURS_OF_REST } from './constants';
import { getRequiredCertificates, isCertificateRequirementMet, parseCategory } from '../../../crew/modals/add-crew/certificateHelpers';
import { filterTombstoned } from '../../../../../sync/tombstone';  // FT-458: a soft-deleted drill must not count as a crew member's "last drill"

/**
 * FT-189: Get crew member display name
 * Canonical helper for consistent name resolution across the app.
 * Priority: name > full_name > fullName > firstName+lastName > 'Unknown'
 *
 * @param {Object|string} crewMember - Crew member object or string (passthrough)
 * @returns {string} Display name
 */
export const getCrewDisplayName = (crewMember) => {
  // String passthrough (already a name)
  if (typeof crewMember === 'string') {
    return crewMember.trim() || 'Unknown';
  }

  // Null/undefined guard
  if (!crewMember) return 'Unknown';

  // Priority: name (canonical) > full_name (legacy) > fullName (variant) > constructed
  const name = (
    crewMember.name ||
    crewMember.full_name ||
    crewMember.fullName ||
    ''
  ).trim();

  if (name) return name;

  // Fallback: construct from firstName/lastName if available
  const firstName = (crewMember.firstName || '').trim();
  const lastName = (crewMember.lastName || '').trim();
  if (firstName || lastName) {
    return lastName && firstName
      ? `${firstName} ${lastName}`
      : firstName || lastName;
  }

  return 'Unknown';
};

/**
 * FT-189: Resolve crew ID to display name using crew list
 * Useful for displaying names in history records, defects, etc.
 *
 * @param {string} crewId - Crew member ID to look up
 * @param {Array} crewList - List of crew members to search
 * @returns {string} Display name or the original ID if not found
 */
export const resolveCrewName = (crewId, crewList = []) => {
  if (!crewId) return 'Unknown';

  // FT-421: Only treat string as a name if it already contains a space (i.e. "First Last").
  // Previously this also short-circuited on !startsWith('CRW-'), which caused raw auth-user
  // UUIDs (e.g. "abc123-...") to pass straight through and render as-is. UUIDs don't contain
  // spaces, so removing that arm forces the crew-list lookup, which may return a display name
  // or fall back to the original string (no worse than before for unknown IDs).
  if (typeof crewId === 'string' && crewId.includes(' ')) {
    return crewId;
  }

  const member = crewList.find(m => m.id === crewId);
  return member ? getCrewDisplayName(member) : crewId;
};

/**
 * Calculate compliance status for a crew member
 */
export const getCrewCompliance = (member, hoursData = {}, vesselConfig = null, rawDrillsData = [], familiarisationRecords = [], familiarisationValidityMonths = 12) => {
  // FT-458: single chokepoint — strip soft-deleted (tombstoned) drills so a
  // deleted drill can never become a crew member's "last drill" and wrongly
  // clear an overdue flag (a deleted record must NEVER improve a safety status).
  const drillsData = filterTombstoned(rawDrillsData);
  const today = new Date();
  const summaryIssues = [];
  let certIssueCount = 0;
  let certWarningCount = 0;
  
  // FT-106 FIX: Check medical in BOTH legacy flat field AND certificates[] array
  // The certificates[] array is the correct source (used by Add Certificate form)
  // Legacy medical_expiry field is kept for backward compatibility
  const certificates = Array.isArray(member.certificates) ? member.certificates : [];
  const medicalCerts = certificates.filter(cert => {
    const certType = cert?.type || '';
    return certType.includes('ENG1') || certType.includes('ML5') || certType.includes('Medical Certificate');
  });

  // Get medical expiry from certificates array OR legacy flat field
  let medicalExpiry = null;
  if (medicalCerts.length > 0) {
    // Find the latest expiry from medical certs
    const validMedicalCert = medicalCerts.find(cert => cert?.expiry_date || cert?.expiry);
    if (validMedicalCert) {
      medicalExpiry = new Date(validMedicalCert.expiry_date || validMedicalCert.expiry);
    }
  } else if (member.medical_expiry) {
    // Fallback to legacy flat field
    medicalExpiry = new Date(member.medical_expiry);
  }

  if (medicalExpiry) {
    const daysUntilExpiry = Math.floor((medicalExpiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < EXPIRY_THRESHOLDS.medical.error) {
      summaryIssues.push({ type: 'error', text: 'Medical expired', category: 'medical' });
    } else if (daysUntilExpiry < EXPIRY_THRESHOLDS.medical.warning) {
      summaryIssues.push({ type: 'warning', text: 'Medical expiring', category: 'medical' });
    }
  } else {
    summaryIssues.push({ type: 'error', text: 'No medical', category: 'medical' });
  }
  
  // Check certificates (summarized)
  if (member.certificates && member.certificates.length > 0) {
    member.certificates.forEach(cert => {
      if (cert.expiry_date) {
        const certExpiry = new Date(cert.expiry_date);
        const daysUntilExpiry = Math.floor((certExpiry - today) / (1000 * 60 * 60 * 24));
        
        if (daysUntilExpiry < EXPIRY_THRESHOLDS.certificate.error) {
          certIssueCount++;
        } else if (daysUntilExpiry < EXPIRY_THRESHOLDS.certificate.warning) {
          certWarningCount++;
        }
      }
    });
    
    if (certIssueCount > 0) {
      summaryIssues.push({ 
        type: 'error', 
        text: certIssueCount === 1 ? '1 cert expired' : `${certIssueCount} certs expired`, 
        category: 'certificate' 
      });
    }
    if (certWarningCount > 0) {
      summaryIssues.push({ 
        type: 'warning', 
        text: certWarningCount === 1 ? '1 cert expiring' : `${certWarningCount} certs expiring`, 
        category: 'certificate' 
      });
    }
  }
  
  // Check required certificates based on position and vessel config
  if (member.position && vesselConfig) {
    // Extract vessel config values (with safe defaults)
    // FT-106: Use parseCategory() to handle "cat6" format (parseInt fails on non-numeric prefix)
    const vesselAreaCategory = parseCategory(vesselConfig.category);
    const vesselEnginePower = vesselConfig.enginePower || 'under1500';
    // FT-29: Use lengthOverall (canonical field name from vesselConfig schema)
    const vesselLength = parseFloat(vesselConfig.lengthOverall || vesselConfig.length) || 0;
    // FT-30: Extract equipment config for conditional cert requirements
    // FT-30 Fix: Include navigationEquipment array for user-selected equipment detection
    const vesselEquipment = {
      hasRadar: vesselConfig.hasRadar || vesselConfig.optionalEquipment?.radar,
      hasECDIS: vesselConfig.hasECDIS || vesselConfig.navcomms?.hasECDIS,
      hasCooking: vesselConfig.hasCooking || vesselConfig.accommodation?.hasCooking,
      hasGalley: vesselConfig.hasGalley,
      operations: vesselConfig.operations?.operations || vesselConfig.operations || [],
      navigationEquipment: vesselConfig.navigationEquipment || [],
      accommodation: vesselConfig.accommodation,
      hasStabilityBooklet: vesselConfig.hasStabilityBooklet
    };

    // Get required certificates for this position
    const requirements = getRequiredCertificates(
      member.position,
      vesselAreaCategory,
      vesselEnginePower,
      vesselLength,
      vesselEquipment
    );

    // Adapt member data to formData format expected by isCertificateRequirementMet
    const memberAsFormData = {
      position: member.position,
      certificates: Array.isArray(member.certificates) ? member.certificates : [],
      medical_expiry: member.medical_expiry,
      medical_type: member.medical_type
    };

    // Track missing certificates by category
    const missingMandatory = [];
    const missingPositionSpecific = [];

    // Check mandatory certificates (skip medical - handled separately above)
    requirements.mandatoryForAll.certificates.forEach(req => {
      if (req.type !== 'medical' && req.required && !isCertificateRequirementMet(req, memberAsFormData)) {
        missingMandatory.push(req.label);
      }
    });

    // Check position-specific certificates
    // FT-106 Bug 3: Store the full requirement object, not just the label
    // This allows showing specific cert options instead of generic "Certificate of Competency"
    requirements.positionSpecific.certificates.forEach(req => {
      if (req.required && !isCertificateRequirementMet(req, memberAsFormData)) {
        missingPositionSpecific.push(req);
      }
    });

    // Add issues for missing mandatory certificates
    if (missingMandatory.length > 0) {
      missingMandatory.forEach(cert => {
        summaryIssues.push({
          type: 'error',
          text: `Missing: ${cert}`,
          category: 'certificate'
        });
      });
    }

    // Add issues for missing position-specific certificates
    // FT-106 Bug 3: Show specific cert options instead of generic label
    missingPositionSpecific.forEach(req => {
      let displayText;
      if (req.options && req.options.length > 0) {
        // Show first option as example for requirements with multiple options
        // e.g., "Missing: CoC (e.g., RYA Powerboat Level 2 Commercial)"
        const firstOption = req.options[0];
        displayText = `Missing: ${req.label} (e.g., ${firstOption})`;
      } else {
        displayText = `Missing: ${req.label}`;
      }
      summaryIssues.push({
        type: 'error',
        text: displayText,
        category: 'certificate'
      });
    });
  }
  
  // Check familiarisation - look in familiarisation records
  const memberFamiliarisations = familiarisationRecords.filter(r => r.crewId === member.id);
  const latestFamiliarisation = memberFamiliarisations
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))[0];

  let hasFamiliarisation = false;
  if (latestFamiliarisation) {
    // Phase 3: Use per-record validity if available, otherwise use global setting
    const recordValidityMonths = latestFamiliarisation.checklistValidityMonths;
    const validityMonths = recordValidityMonths !== undefined
      ? recordValidityMonths
      : familiarisationValidityMonths;

    // CRW-005B: Handle "no expiry" (0 = never expires)
    if (validityMonths === 0) {
      hasFamiliarisation = true;
    } else {
      const completedDate = new Date(latestFamiliarisation.completedAt);
      const expiryDate = new Date(completedDate);
      expiryDate.setMonth(expiryDate.getMonth() + validityMonths);
      hasFamiliarisation = expiryDate > new Date();
    }
  }

  // Also check legacy member property as fallback
  if (!hasFamiliarisation && !member.familiarisation_completed) {
    summaryIssues.push({ type: 'warning', text: 'Familiarisation required', category: 'familiarisation' });
  }
  
  // Check Hours of Rest (if active crew)
  if (member.status === 'Active' || member.status === 'active') {
    const memberHours = hoursData[member.id] || {};
    const lastWeek = [];
    const weekStart = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() - i);
      lastWeek.push(date.toISOString().split('T')[0]);
    }
    
    let weeklyHours = 0;
    let hasViolation = false;
    lastWeek.forEach(date => {
      const dayData = memberHours[date];
      if (dayData) {
        weeklyHours += dayData.totalRest || 0;
        if (dayData.totalRest < HOURS_OF_REST.minimumDaily) hasViolation = true;
      } else {
        weeklyHours += 24;
      }
    });
    
    if (weeklyHours < HOURS_OF_REST.minimumWeekly || hasViolation) {
      summaryIssues.push({ type: 'error', text: 'HOR non-compliant', category: 'hours' });
    }
  }
  
  // Check drill participation (if active crew and drill data provided)
  if ((member.status === 'Active' || member.status === 'active') && drillsData.length > 0 && vesselConfig) {
    // Import drill requirements dynamically to avoid circular dependency
    const { getDrillRequirements } = require('../../../../../config/drillRequirements');
    const vesselRequirements = getDrillRequirements(vesselConfig);
    const requiredDrills = Object.values(vesselRequirements).filter(req => req.compliance_level === 'Required');
    
    let drillsOverdue = 0;
    requiredDrills.forEach(requirement => {
      // Find last drill of this type that the crew member participated in
      const participatedDrills = drillsData.filter(drill => 
        drill.type === requirement.drill_type && 
        drill.participants?.some(p => p.id === member.id || p === member.id)
      );
      
      const lastDrill = participatedDrills
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      
      if (lastDrill) {
        const daysSince = Math.floor((today - new Date(lastDrill.date)) / (1000 * 60 * 60 * 24));
        if (daysSince > requirement.frequency_days) {
          drillsOverdue++;
        }
      } else {
        // Never participated in this required drill
        drillsOverdue++;
      }
    });
    
    if (drillsOverdue > 0) {
      summaryIssues.push({ 
        type: 'warning', 
        text: drillsOverdue === 1 ? '1 drill overdue' : `${drillsOverdue} drills overdue`, 
        category: 'drills' 
      });
    }
  }
  
  // Return summary or compliant
  if (summaryIssues.length === 0) {
    return { type: 'compliant', text: '✓ OK', issues: [] };
  }
  
  // Sort by severity (errors first)
  summaryIssues.sort((a, b) => {
    if (a.type === 'error' && b.type !== 'error') return -1;
    if (a.type !== 'error' && b.type === 'error') return 1;
    return 0;
  });
  
  // Determine overall status
  const hasError = summaryIssues.some(i => i.type === 'error');
  const overallType = hasError ? 'error' : 'warning';
  
  return { 
    type: overallType, 
    text: summaryIssues.length > 1 ? `${summaryIssues.length} issues` : summaryIssues[0].text,
    issues: summaryIssues 
  };
};

/**
 * Helper to check if crew member is on duty
 * FT-90 FIX: Now uses dutyStatus[vesselId] map like useCrew.js/useFleetCrew.js
 * Falls back to legacy status field for backwards compat
 */
const isOnDutyHelper = (member, vesselId = null) => {
  // FT-90: Check dutyStatus map first (per-vessel duty status)
  if (vesselId && member?.dutyStatus) {
    return member.dutyStatus[vesselId] === 'on_duty';
  }
  // Legacy fallback: check flat status field
  const status = (member?.status || 'active').toLowerCase().replace(/\s+/g, '');
  return status === 'active' || status === 'onduty';
};

/**
 * Calculate crew statistics
 * Updated 2026-03-04: Uses isOnDutyHelper for proper counting of On Duty/Off Duty
 * FT-90: Added onDutyCompliant/onDutyIssues for dual-view SMS compliance
 * FT-90 FIX: Now accepts vesselId to properly check dutyStatus[vesselId] map
 */
export const calculateCrewStats = (crewMembers, hoursData, vesselConfig = null, drillsData = [], familiarisationRecords = [], familiarisationValidityMonths = 12, vesselId = null) => {
  const stats = {
    total: crewMembers.length,
    active: 0,           // Now means "On Duty" (includes 'active' and 'on duty' status)
    onLeave: 0,
    offDuty: 0,          // Anyone not on duty and not archived
    compliant: 0,        // All crew compliant (for planning view)
    warnings: 0,
    issues: 0,
    // FT-90: On Duty specific compliance stats for SMS Compliance view
    onDutyCompliant: 0,  // On duty crew with no issues
    onDutyIssues: 0      // On duty crew with issues
  };

  crewMembers.forEach(member => {
    // FT-90 FIX: Pass vesselId to properly check dutyStatus map
    const isOnDuty = isOnDutyHelper(member, vesselId);

    // Status counts - use isOnDutyHelper for consistency with UI
    if (isOnDuty) {
      stats.active++;
    } else if ((member.status || '').toLowerCase() === 'on leave') {
      stats.onLeave++;
    } else if (member.status !== 'archived') {
      // Off Duty: anyone NOT on duty and NOT archived
      stats.offDuty++;
    }

    // Compliance counts (all crew)
    // FT-148 FIX: Warnings count as compliant (e.g., cert expiring in 30 days is not a compliance failure)
    const compliance = getCrewCompliance(member, hoursData, vesselConfig, drillsData, familiarisationRecords, familiarisationValidityMonths);
    if (compliance.type === 'compliant' || compliance.type === 'warning') stats.compliant++;
    else if (compliance.type === 'error') stats.issues++;
    // Track warnings separately for UI display purposes
    if (compliance.type === 'warning') stats.warnings++;

    // FT-90: On Duty specific compliance (for SMS Compliance calculation)
    // MCA auditors focus on current operational crew readiness
    // FT-148 FIX: Warnings count as compliant for on-duty crew too
    if (isOnDuty) {
      if (compliance.type === 'compliant' || compliance.type === 'warning') {
        stats.onDutyCompliant++;
      } else if (compliance.type === 'error') {
        stats.onDutyIssues++;
      }
    }
  });

  return stats;
};

/**
 * Export crew data to CSV
 */
export const exportCrewToCSV = (crewMembers, filename = 'crew-list') => {
  const headers = ['Name', 'ID', 'Position', 'Status', 'Phone', 'Email', 'Medical Type', 'Medical Expiry', 'Certificates'];
  const rows = crewMembers.map(member => [
    member.full_name,
    member.id,
    member.position,
    member.status || 'Active',
    member.phone || '',
    member.email || '',
    member.medical_type || '',
    member.medical_expiry || '',
    member.certificates?.map(c => `${c.type} (expires: ${c.expiry_date})`).join('; ') || ''
  ]);
  
  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');
  
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Filter crew members based on search and filter criteria
 */
export const filterCrewMembers = (crewMembers, { searchTerm = '', statusFilter = 'all', complianceFilter = 'all', hoursData = {}, vesselConfig = null, drillsData = [], familiarisationRecords = [], familiarisationValidityMonths = 12 }) => {
  return crewMembers.filter(member => {
    // Search filter
    const search = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || (
      member.full_name?.toLowerCase().includes(search) ||
      member.position?.toLowerCase().includes(search) ||
      member.id?.toLowerCase().includes(search) ||
      member.email?.toLowerCase().includes(search)
    );

    // Status filter
    const matchesStatus = statusFilter === 'all' ||
      (member.status || 'Active').toLowerCase() === statusFilter.toLowerCase();

    // Compliance filter
    let matchesCompliance = true;
    if (complianceFilter !== 'all') {
      const compliance = getCrewCompliance(member, hoursData, vesselConfig, drillsData, familiarisationRecords, familiarisationValidityMonths);
      matchesCompliance = compliance.type === complianceFilter;
    }

    return matchesSearch && matchesStatus && matchesCompliance;
  });
};